import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const CHUNK = 20 * 1024 * 1024;

function fmtBytes(n) {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.round(n / 1024)} KB`;
}

const STATUS_STYLE = {
  uploading: "text-sky-400",
  processing: "text-[var(--pm-orange)]",
  done: "text-emerald-400",
  failed: "text-red-400",
  cancelled: "text-white/40",
};

export default function ZipUpload({ events, onUploaded }) {
  const { user } = useAuth();
  const defaultCur = (user?.default_currency || "gbp").toLowerCase();
  const [file, setFile] = useState(null);
  const [eventId, setEventId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState(defaultCur);
  const [titlePrefix, setTitlePrefix] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sent, setSent] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [dedupe, setDedupe] = useState(null);
  const [dedupeStatus, setDedupeStatus] = useState(null);
  const cancelRef = useRef(false);
  const prevStatuses = useRef({});

  const groups = events.find((e) => e.id === eventId)?.groups || [];

  const refreshJobs = () => {
    return api.get("/admin/zip/jobs").then((r) => {
      const list = r.data.jobs || [];
      for (const j of list) {
        if (prevStatuses.current[j.id] === "processing" && j.status === "done") {
          toast.success(`Zip "${j.filename}" processed — ${j.done} photos published`);
          onUploaded?.();
        }
        prevStatuses.current[j.id] = j.status;
      }
      setJobs(list);
      return list;
    }).catch(() => []);
  };

  useEffect(() => { refreshJobs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const active = jobs.some((j) => j.status === "processing" || j.status === "uploading");
    if (!active) return;
    const t = setInterval(refreshJobs, 3000);
    return () => clearInterval(t);
  }, [jobs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pushes chunks from startIdx onward. Retries each chunk up to 8 times with
  // growing backoff so a server restart (~60s) doesn't kill a 9 GB upload.
  const uploadChunks = async (uploadId, theFile, startIdx) => {
    const totalChunks = Math.ceil(theFile.size / CHUNK);
    setSent(Math.min(startIdx * CHUNK, theFile.size));
    for (let idx = startIdx; idx < totalChunks; idx++) {
      if (cancelRef.current) throw new Error("Cancelled");
      const blob = theFile.slice(idx * CHUNK, Math.min((idx + 1) * CHUNK, theFile.size));
      let attempts = 0;
      for (;;) {
        try {
          const fd = new FormData();
          fd.append("index", String(idx));
          fd.append("chunk", blob, "chunk.bin");
          await api.post(`/admin/zip/chunk/${uploadId}`, fd, { timeout: 5 * 60 * 1000 });
          break;
        } catch (e) {
          if (cancelRef.current) throw new Error("Cancelled");
          attempts += 1;
          if (attempts >= 8) throw e;
          await new Promise((r) => setTimeout(r, Math.min(2000 * 2 ** (attempts - 1), 15000)));
        }
      }
      setSent(Math.min((idx + 1) * CHUNK, theFile.size));
    }
    await api.post(`/admin/zip/complete/${uploadId}`);
    toast.success("Upload complete — processing started. You can leave this page.");
    setFile(null);
  };

  const start = async () => {
    if (!file) return toast.error("Pick a .zip file first");
    if (!eventId) return toast.error("Select an event");
    if (!groupId) return toast.error("Select a default class");
    if (price === "" || Number(price) < 0) return toast.error("Price required (0 or more)");

    setUploading(true);
    setSent(0);
    cancelRef.current = false;
    try {
      const init = await api.post("/admin/zip/init", {
        filename: file.name,
        size: file.size,
        event_id: eventId,
        group_id: groupId,
        price: Number(price),
        currency,
        title_prefix: titlePrefix,
      });
      await uploadChunks(init.data.upload_id, file, 0);
    } catch (e) {
      if (e?.message === "Cancelled") {
        toast.info("Upload cancelled");
      } else {
        toast.error(`Upload interrupted — ${formatApiError(e)}. Your progress is saved: press Resume on the job below.`);
      }
    } finally {
      setUploading(false);
      refreshJobs();
    }
  };

  // Resume an interrupted upload: needs the same zip re-selected (or still in memory).
  const resumeJob = async (job) => {
    if (!file || file.name !== job.filename || file.size !== job.size) {
      return toast.error(`Select the same zip file first (${job.filename}, ${fmtBytes(job.size)})`);
    }
    setUploading(true);
    cancelRef.current = false;
    try {
      const list = await refreshJobs();
      const fresh = list.find((j) => j.id === job.id);
      if (!fresh || fresh.status !== "uploading") throw new Error("This upload can no longer be resumed");
      await uploadChunks(job.id, file, fresh.next_index || 0);
    } catch (e) {
      if (e?.message === "Cancelled") {
        toast.info("Upload cancelled");
      } else {
        toast.error(`Upload interrupted — ${formatApiError(e)}. Press Resume to continue.`);
      }
    } finally {
      setUploading(false);
      refreshJobs();
    }
  };

  const scanDupes = () => {
    api.post("/admin/zip/dedupe", { dry_run: true })
      .then((r) => setDedupe(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  };

  const runDedupe = () => {
    api.post("/admin/zip/dedupe", { dry_run: false })
      .then(() => {
        setDedupe(null);
        setDedupeStatus({ status: "running", deleted: 0 });
        toast.success("Cleanup started — removing duplicate copies in the background");
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => {
    if (dedupeStatus?.status !== "running") return;
    const t = setInterval(() => {
      api.get("/admin/zip/dedupe/status").then((r) => {
        setDedupeStatus(r.data);
        if (r.data.status === "done") {
          toast.success(`Cleanup complete — ${r.data.deleted} duplicate copies removed`);
          onUploaded?.();
        }
      }).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [dedupeStatus?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelJob = (id) => {
    api.post(`/admin/zip/cancel/${id}`).then(refreshJobs).catch((e) => toast.error(formatApiError(e)));
  };

  const pct = file && uploading ? Math.round((sent / file.size) * 100) : 0;

  return (
    <div className="space-y-8" data-testid="zip-upload-panel">
      <div className="border border-white/10 bg-[#0a0a0a] p-6 space-y-5">
        <div>
          <h3 className="text-sm uppercase tracking-[0.3em] font-semibold">Upload a ZIP</h3>
          <p className="text-xs text-white/50 mt-2 font-condensed">
            Upload one zip and walk away — the server unpacks, watermarks and publishes everything in the
            background. Subfolders inside the zip become classes automatically; loose files go to the
            default class you pick below.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.25em] text-white/40 block mb-2">Event</label>
            <select
              value={eventId}
              onChange={(e) => { setEventId(e.target.value); setGroupId(""); }}
              className="input-minimal w-full"
              disabled={uploading}
              data-testid="zip-event-select"
            >
              <option value="">Select event…</option>
              {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.date} · {ev.track}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.25em] text-white/40 block mb-2">Default class</label>
            <select
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              className="input-minimal w-full"
              disabled={uploading || !eventId}
              data-testid="zip-group-select"
            >
              <option value="">Select class…</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.25em] text-white/40 block mb-2">Price per photo</label>
            <input
              type="number" min="0" step="0.5"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="5.00"
              className="input-minimal w-full"
              disabled={uploading}
              data-testid="zip-price-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.25em] text-white/40 block mb-2">Title prefix (optional)</label>
            <input
              value={titlePrefix}
              onChange={(e) => setTitlePrefix(e.target.value)}
              placeholder="e.g. Race 2"
              className="input-minimal w-full"
              disabled={uploading}
              data-testid="zip-prefix-input"
            />
          </div>
        </div>

        <div>
          <input
            type="file"
            accept=".zip,application/zip"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            disabled={uploading}
            className="block text-sm text-white/70 file:mr-4 file:py-3 file:px-6 file:border file:border-white/20 file:bg-transparent file:text-white file:text-xs file:uppercase file:tracking-[0.2em] file:cursor-pointer hover:file:border-white/60"
            data-testid="zip-file-input"
          />
          {file && !uploading && (
            <p className="text-xs text-white/50 mt-2">{file.name} — {fmtBytes(file.size)}</p>
          )}
        </div>

        {uploading ? (
          <div className="space-y-3" data-testid="zip-upload-progress">
            <div className="flex justify-between text-xs text-white/60">
              <span>Uploading {file?.name}</span>
              <span>{fmtBytes(sent)} / {fmtBytes(file?.size || 0)} · {pct}%</span>
            </div>
            <div className="h-2 bg-white/10">
              <div className="h-2 bg-[var(--pm-orange)] transition-all" style={{ width: `${pct}%` }} />
            </div>
            <button
              onClick={() => { cancelRef.current = true; }}
              className="text-xs uppercase tracking-[0.2em] text-red-400 hover:text-red-300"
              data-testid="zip-cancel-upload-button"
            >
              Cancel upload
            </button>
          </div>
        ) : (
          <button
            onClick={start}
            className="bg-white text-black px-8 py-4 text-xs uppercase tracking-[0.3em] font-medium hover:bg-white/90 transition disabled:opacity-40"
            disabled={!file}
            data-testid="zip-start-button"
          >
            Upload zip
          </button>
        )}
      </div>

      {jobs.length > 0 && (
        <div className="border border-white/10 bg-[#0a0a0a] p-6" data-testid="zip-jobs-list">
          <h3 className="text-sm uppercase tracking-[0.3em] font-semibold mb-4">Zip jobs</h3>
          <div className="space-y-3">
            {jobs.map((j) => (
              <div key={j.id} className="border border-white/10 p-4" data-testid="zip-job-row">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm truncate">{j.filename}</p>
                    <p className="text-xs text-white/40 mt-1">
                      {j.event_name} · {fmtBytes(j.size)} · {new Date(j.created_at).toLocaleString("en-GB")}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`text-xs uppercase tracking-[0.2em] font-semibold ${STATUS_STYLE[j.status] || "text-white/60"}`}>
                      {j.status}
                    </span>
                    {j.status === "uploading" && !uploading && (
                      <button
                        onClick={() => resumeJob(j)}
                        className="text-xs uppercase tracking-[0.2em] text-emerald-400 hover:text-emerald-300"
                        data-testid="zip-resume-button"
                      >
                        Resume
                      </button>
                    )}
                    {(j.status === "processing" || j.status === "uploading") && (
                      <button
                        onClick={() => cancelJob(j.id)}
                        className="text-xs uppercase tracking-[0.2em] text-red-400 hover:text-red-300"
                        data-testid="zip-cancel-button"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
                {j.status === "uploading" && (
                  <p className="text-xs text-white/50 mt-2">
                    {fmtBytes(j.received || 0)} of {fmtBytes(j.size)} received
                    {!uploading ? " — select the same zip file above and press Resume to continue" : ""}
                  </p>
                )}
                {j.status === "processing" && (
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-white/60 mb-1">
                      <span>Watermarking & publishing…</span>
                      <span>{j.done} / {j.total || "…"}</span>
                    </div>
                    <div className="h-1.5 bg-white/10">
                      <div
                        className="h-1.5 bg-[var(--pm-orange)] transition-all"
                        style={{ width: j.total ? `${Math.round((j.done / j.total) * 100)}%` : "5%" }}
                      />
                    </div>
                  </div>
                )}
                {j.status === "done" && (
                  <p className="text-xs text-white/50 mt-2">
                    {j.done - (j.errors?.length || 0)} photos published
                    {j.groups_created?.length ? ` · classes created: ${j.groups_created.join(", ")}` : ""}
                  </p>
                )}
                {j.status === "failed" && j.error && (
                  <p className="text-xs text-red-400 mt-2">{j.error}</p>
                )}
                {j.errors?.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs text-red-400 cursor-pointer">{j.errors.length} file error(s)</summary>
                    <ul className="text-xs text-white/50 mt-1 space-y-0.5 max-h-32 overflow-auto">
                      {j.errors.map((e, i) => <li key={i}>✗ {e.filename} — {e.error}</li>)}
                    </ul>
                  </details>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {["admin", "super_admin"].includes(user?.role) && (
        <div className="border border-white/10 bg-[#0a0a0a] p-6" data-testid="zip-dedupe-card">
          <h3 className="text-sm uppercase tracking-[0.3em] font-semibold">Duplicate photo cleanup</h3>
          <p className="text-xs text-white/50 mt-2 font-condensed">
            Finds photos published more than once by an interrupted zip import. The earliest copy of
            each photo is kept; purchased photos are never touched.
          </p>
          <div className="mt-4 space-y-3">
            {dedupeStatus?.status === "running" ? (
              <p className="text-xs text-[var(--pm-orange)]" data-testid="zip-dedupe-progress">
                Removing duplicates… {dedupeStatus.deleted || 0}{dedupeStatus.total ? ` / ${dedupeStatus.total}` : ""}
              </p>
            ) : dedupe ? (
              dedupe.to_delete > 0 ? (
                <div className="space-y-3">
                  <p className="text-xs text-white/70" data-testid="zip-dedupe-result">
                    {dedupe.duplicate_photos} photos have extra copies — {dedupe.to_delete} duplicates can be removed
                    {dedupe.skipped_purchased > 0 ? ` (${dedupe.skipped_purchased} skipped: purchased)` : ""}.
                  </p>
                  <button
                    onClick={runDedupe}
                    className="border border-red-400/50 text-red-400 px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-red-400/10"
                    data-testid="zip-dedupe-run-button"
                  >
                    Remove {dedupe.to_delete} duplicates
                  </button>
                </div>
              ) : (
                <p className="text-xs text-emerald-400" data-testid="zip-dedupe-result">No duplicates found ✓</p>
              )
            ) : (
              <button
                onClick={scanDupes}
                className="border border-white/20 px-6 py-3 text-xs uppercase tracking-[0.2em] hover:border-white/60"
                data-testid="zip-dedupe-scan-button"
              >
                Scan for duplicates
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
